// Pure helpers shared across Google Ads panels.
// Includes search-term classification, exports, and comparison-label generation.
// No React.

/* ----------------------------- text helpers ----------------------------- */
export const stripMatchSymbols = (keyword) => (keyword || '').replace(/^\[|]$|^"|"$/g, '');

// Responsive Search Ads pack every headline into one pipe-delimited `ad_title`
// string. Split it back into individual headline assets. Expanded Text Ads (and
// any ad with no asset list) come back as a bare "Ad <id>" — treat that as "no
// assets" so callers can show a friendly label instead of the raw id.
const AD_ID_ONLY = /^Ad\s+\d+$/i;
export const parseAdAssets = (adTitle) => {
  if (!adTitle || AD_ID_ONLY.test(adTitle.trim())) return [];
  return adTitle.split('|').map((part) => part.trim()).filter(Boolean);
};

// Human label for an ad type key (e.g. "RESPONSIVE_SEARCH_AD" → "Responsive Search Ad").
export const prettyAdType = (adType) => {
  const raw = (adType || '').replace(/_/g, ' ').toLowerCase();
  if (!raw) return 'Ad';
  return raw.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

// Title-case an INTERNAL label (ad-group / campaign name), fixing common
// all-caps acronyms. Safe for internal names only — never for ad headlines,
// which must stay verbatim so we don't misrepresent live creative.
const LABEL_ACRONYMS = { Rv: 'RV', Az: 'AZ', Us: 'US', Bbq: 'BBQ', Ac: 'AC', Koa: 'KOA' };
export const titleCaseLabel = (value) => {
  if (!value) return value;
  return value
    .split(/\s+/)
    .map((word) => {
      const cased = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      return LABEL_ACRONYMS[cased] || cased;
    })
    .join(' ');
};

export const normalizeInsight = (text) => (text || '').replace(/\b[A-Z]{4,}\b/g, (word) =>
  word.charAt(0) + word.slice(1).toLowerCase()
);

/* ----------------------------- search-term categorization -----------------------------
   Internal vocabulary used by buildSearchTermCategories / categorizeSearchTerm.
   These are not exported because they're implementation details of the classifier.
*/
const AMENITY_TERMS = [
  'pool', 'swimming', 'swim', 'heated pool', 'splash pad', 'water park', 'lazy river', 'hot tub', 'jacuzzi', 'spa',
  'hookup', 'hook up', 'full hookup', 'full hook up', '30 amp', '50 amp', 'electric', 'sewer', 'water hookup', 'dump station', 'electric hookup',
  'wifi', 'wi-fi', 'internet', 'wireless',
  'pull through', 'pull-through', 'pullthrough', 'back in', 'back-in', 'big rig', 'big rig friendly', 'large site', 'spacious', 'concrete pad', 'paved', 'level site', 'patio',
  'waterfront', 'lakefront', 'lakeside', 'riverside', 'beachfront', 'ocean', 'beach', 'lake', 'river', 'creek', 'water view', 'boat launch', 'boat ramp', 'dock', 'marina', 'fishing', 'kayak', 'canoe',
  'playground', 'hiking', 'trail', 'bike', 'pickleball', 'basketball', 'volleyball', 'golf', 'mini golf', 'game room', 'arcade', 'rec hall', 'recreation', 'clubhouse', 'fitness', 'gym', 'sport court',
  'kid friendly', 'family friendly', 'family', 'kids', 'children', 'bouncing pillow', 'jumping pillow',
  'pet friendly', 'pets', 'dog friendly', 'dog park', 'dog run', 'pet', 'dogs allowed', 'dog wash',
  'laundry', 'shower', 'restroom', 'bathroom', 'bath house', 'bathhouse', 'camp store', 'general store', 'store', 'propane', 'firewood', 'fire pit', 'firepit', 'fire ring',
  'restaurant', 'bar', 'grill', 'cafe', 'food', 'snack bar',
  'activities', 'entertainment', 'live music', 'events',
  'scenic', 'views', 'mountain', 'desert', 'forest', 'wooded', 'shaded', 'shade', 'trees', 'quiet', 'secluded', 'private', 'peaceful', 'nature',
];
const LONG_TERM_TERMS = [
  'monthly', 'month to month', 'month-to-month', 'long term', 'long-term', 'longterm', 'seasonal', 'season',
  'winter', 'snowbird', 'snow bird', 'extended stay', 'extended', 'annual', 'yearly', 'full time', 'full-time',
  'permanent', 'semi-permanent', '6 month', 'six month', '3 month', 'three month',
  'weekly rate', 'monthly rate', 'monthly rental', 'winter rental', 'winter stay', 'summer stay',
  'lease', 'lot rental', 'park model', 'park home', 'resident', 'residential',
  'retire', 'retirement', '55+', '55 plus', 'senior', 'age restricted', 'adult only', 'adults only',
  'workamper', 'workcamp', 'work camping',
];
const ACCOMMODATION_TERMS = [
  'rv park', 'rv resort', 'rv campground', 'rv camp', 'rv site', 'rv spot', 'rv space', 'rv lot', 'rv camping',
  'motorhome', 'motor home', 'travel trailer', 'fifth wheel', '5th wheel', 'class a', 'class b', 'class c',
  'camper', 'campervan', 'van life', 'toy hauler',
  'campground', 'campsite', 'camp site', 'camping', 'tent site', 'tent camping', 'tent', 'primitive', 'primitive camping', 'dry camping', 'boondocking', 'boondock',
  'glamping', 'glamp', 'yurt', 'treehouse', 'tree house', 'safari tent', 'bell tent', 'luxury camping', 'luxury tent',
  'dome', 'geodesic', 'a-frame', 'a frame', 'tiny home', 'tiny house', 'unique stay',
  'cabin', 'cabins', 'cottage', 'lodge', 'chalet', 'bungalow', 'rental cabin', 'cabin rental', 'rustic cabin', 'log cabin', 'deluxe cabin',
];
const LOCATION_PATTERNS = [
  'near me', 'nearby', 'close to', 'close by', 'around here', 'driving distance', 'miles from', 'minutes from', 'hours from',
];
const NAME_SUFFIXES = [
  'rv resort', 'rv park', 'campground', 'camp', 'camping', 'resort', 'glamping',
  'retreat', 'lodge', 'ranch', 'village', 'landing', 'cove', 'haven', 'oasis', 'hideaway', 'getaway',
];
const STATE_ABBR = {
  'alabama':'al','alaska':'ak','arizona':'az','arkansas':'ar','california':'ca','colorado':'co',
  'connecticut':'ct','delaware':'de','florida':'fl','georgia':'ga','hawaii':'hi','idaho':'id',
  'illinois':'il','indiana':'in','iowa':'ia','kansas':'ks','kentucky':'ky','louisiana':'la',
  'maine':'me','maryland':'md','massachusetts':'ma','michigan':'mi','minnesota':'mn','mississippi':'ms',
  'missouri':'mo','montana':'mt','nebraska':'ne','nevada':'nv','new hampshire':'nh','new jersey':'nj',
  'new mexico':'nm','new york':'ny','north carolina':'nc','north dakota':'nd','ohio':'oh','oklahoma':'ok',
  'oregon':'or','pennsylvania':'pa','rhode island':'ri','south carolina':'sc','south dakota':'sd',
  'tennessee':'tn','texas':'tx','utah':'ut','vermont':'vt','virginia':'va','washington':'wa',
  'west virginia':'wv','wisconsin':'wi','wyoming':'wy',
  'alberta':'ab','british columbia':'bc','manitoba':'mb','new brunswick':'nb','newfoundland and labrador':'nl',
  'nova scotia':'ns','ontario':'on','prince edward island':'pe','quebec':'qc','saskatchewan':'sk',
};

export const buildSearchTermCategories = (clientInfo) => {
  const brandTerms = [];
  if (clientInfo?.campground_name) {
    const fullName = clientInfo.campground_name.toLowerCase().trim();
    brandTerms.push(fullName);
    for (const suffix of NAME_SUFFIXES) {
      if (fullName.endsWith(suffix)) {
        const core = fullName.replace(new RegExp(suffix + '$'), '').trim();
        if (core.length > 2) brandTerms.push(core);
      }
    }
  }

  const locationTerms = [];
  if (clientInfo?.city) locationTerms.push(clientInfo.city.toLowerCase());
  if (clientInfo?.state) {
    const stateCode = clientInfo.state.toLowerCase();
    locationTerms.push(stateCode);
    if (stateCode.length === 2) {
      const fullName = Object.entries(STATE_ABBR).find(([, abbr]) => abbr === stateCode)?.[0];
      if (fullName) locationTerms.push(fullName);
    } else {
      const abbr = STATE_ABBR[stateCode];
      if (abbr) locationTerms.push(abbr);
    }
  }

  return { brandTerms, locationTerms };
};

export const categorizeSearchTerm = (term, cats) => {
  const lower = term.toLowerCase();
  const tags = [];
  if (cats.brandTerms.length && cats.brandTerms.some(phrase => lower.includes(phrase))) tags.push('brand');
  if ((cats.locationTerms.length && cats.locationTerms.some(phrase => lower.includes(phrase))) ||
      LOCATION_PATTERNS.some(phrase => lower.includes(phrase))) tags.push('location');
  if (AMENITY_TERMS.some(phrase => lower.includes(phrase))) tags.push('amenity');
  if (LONG_TERM_TERMS.some(phrase => lower.includes(phrase))) tags.push('longterm');
  if (ACCOMMODATION_TERMS.some(phrase => lower.includes(phrase))) tags.push('accommodation');
  return tags;
};

/* ----------------------------- request param helpers ----------------------------- */
// Build the `p_campaigns` value for the Google Ads RPCs. These expect a Postgres
// text[] ARRAY, so return the array as-is (not a comma-joined string — joining
// produced `"Cabins Sedona,Glamping"`, which PostgREST rejected with
// `malformed array literal` once 2+ campaigns were selected). Returns undefined
// when nothing is selected so callers treat it as "no filter" (RPCs coalesce
// undefined/null → full roster).
export const buildCampaignsParam = (selected) =>
  selected.length > 0 ? [...selected] : undefined;

/* ----------------------------- export helpers -----------------------------
 * CSV / PDF download helpers live in the cross-feature src/components/shared/exportData.
 * Re-exported here so existing Google Ads callers (`import { downloadCsv, downloadPdf } from "../shared"`) keep working.
 * `makeExport` builds a single-spec CSV+PDF column definition (no drift).
 */
export { downloadCsv, downloadPdf, makeExport } from '../../shared/exportData';

/* ----------------------------- comparison label ----------------------------- */
export const getComparisonLabel = (comparisonMode, rangeStart, rangeEnd) => {
  if (comparisonMode === 'none') return null;
  const start = new Date(rangeStart + 'T00:00:00');
  const end = new Date(rangeEnd + 'T00:00:00');
  const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24));
  const fmt = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (comparisonMode === 'previous_period') {
    const compEnd = new Date(start);
    compEnd.setDate(compEnd.getDate() - 1);
    const compStart = new Date(compEnd);
    compStart.setDate(compStart.getDate() - daysDiff);
    return `Comparing ${fmt(start)} – ${fmt(end)} vs ${fmt(compStart)} – ${fmt(compEnd)}`;
  }
  if (comparisonMode === 'previous_year') {
    const compStart = new Date(start);
    compStart.setFullYear(compStart.getFullYear() - 1);
    const compEnd = new Date(end);
    compEnd.setFullYear(compEnd.getFullYear() - 1);
    return `Comparing ${fmt(start)} – ${fmt(end)} vs ${fmt(compStart)} – ${fmt(compEnd)}`;
  }
  return null;
};
